import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, withModifiers as _withModifiers, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, vShow as _vShow, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, withDirectives as _withDirectives } from "vue"

import {computed, onMounted, onUnmounted, ref, toRefs, watch} from 'vue';
import {storeToRefs} from 'pinia';
import {usePopupStore} from "@/store/AutocompletePopup/PopupStore";

import AutocompleteInputContainer from '@/components/AutocompletePopup/AutocompleteInputContainer.vue';
import LinearLoader from '@/components/LinearLoader.vue';
import OverlayLoader from '@/components/OverlayLoader.vue';
import AutocompleteContent from '@/components/AutocompletePopup/AutocompleteContainer.vue';
import {useMobileViewStore} from "@/store/MobileViewStore";
import {useBridgeStore} from "@/store/BridgeStore";


export default /*@__PURE__*/_defineComponent({
  __name: 'AutocompletePopup',
  setup(__props) {

const popupStore = usePopupStore()
const {showAutocompletePopup, loading} = toRefs(popupStore)
const mobileViewStore = useMobileViewStore();
const bridgeStore = useBridgeStore()
const { isMobileView } = storeToRefs(mobileViewStore)
const { forceBodyPlacement } = storeToRefs(bridgeStore)
const popupRoot = ref<HTMLElement | null>(null)
const pointerReleaseGuardActive = ref(false)
let pointerReleaseGuardTimer: ReturnType<typeof setTimeout> | null = null
let unregisterPopupSafe: (() => void) | null = null

const deactivatePointerReleaseGuard = () => {
  pointerReleaseGuardActive.value = false
  if (pointerReleaseGuardTimer) {
    clearTimeout(pointerReleaseGuardTimer)
    pointerReleaseGuardTimer = null
  }
}

const activatePointerReleaseGuard = () => {
  if (!isMobileView.value) {
    return
  }
  pointerReleaseGuardActive.value = true
  if (pointerReleaseGuardTimer) {
    clearTimeout(pointerReleaseGuardTimer)
  }
  pointerReleaseGuardTimer = setTimeout(() => {
    pointerReleaseGuardActive.value = false
    pointerReleaseGuardTimer = null
  }, 250)
}

const guardMobilePointerRelease = (event: PointerEvent | MouseEvent | TouchEvent) => {
  if (!pointerReleaseGuardActive.value) {
    return
  }
  event.stopImmediatePropagation()
  event.stopPropagation()
  event.preventDefault()
  deactivatePointerReleaseGuard()
}

const syncPopupSafeElement = () => {
  unregisterPopupSafe?.()
  if (popupRoot.value) {
    unregisterPopupSafe = popupStore.registerSafeElement(popupRoot.value)
  }
}

watch(popupRoot, () => {
  syncPopupSafeElement()
})

watch(showAutocompletePopup, (isOpen, wasOpen) => {
  if (isOpen && !wasOpen) {
    activatePointerReleaseGuard()
  }
})

const isBridgeActive = computed(() => bridgeStore.hasClientInput && !isMobileView.value)
const useBridgePlacement = computed(() => isBridgeActive.value && !forceBodyPlacement.value)

const bridgePopupStyle = computed(() => {
  if (!useBridgePlacement.value || !bridgeStore.popupPlacement) {
    return {}
  }

  const { left, top, width, arrowOffset } = bridgeStore.popupPlacement

  return {
    position: 'fixed',
    left: `${left}px`,
    top: `${top}px`,
    width: `${width}px`,
    maxWidth: `${width}px`,
    minWidth: `${width}px`,
    transform: 'none',
    '--bridge-arrow-left': `${arrowOffset}px`
  }
})

const updateBridgePlacement = () => {
  if (!useBridgePlacement.value) {
    return
  }
  bridgeStore.updatePopupPlacement()
}

let bridgeListenersAttached = false

const attachBridgeListeners = () => {
  if (bridgeListenersAttached) {
    return
  }
  window.addEventListener('resize', updateBridgePlacement)
  window.addEventListener('scroll', updateBridgePlacement, true)
  bridgeListenersAttached = true
}

const detachBridgeListeners = () => {
  if (!bridgeListenersAttached) {
    return
  }
  window.removeEventListener('resize', updateBridgePlacement)
  window.removeEventListener('scroll', updateBridgePlacement, true)
  bridgeListenersAttached = false
}

watch([showAutocompletePopup, isMobileView], ([isOpen, mobile]) => {
  if (isOpen && !mobile && useBridgePlacement.value) {
    updateBridgePlacement()
    attachBridgeListeners()
  } else {
    detachBridgeListeners()
  }
})

watch(isBridgeActive, (active) => {
  if (active && showAutocompletePopup.value && !forceBodyPlacement.value) {
    updateBridgePlacement()
    attachBridgeListeners()
  } else if (!active) {
    detachBridgeListeners()
  }
})

watch(forceBodyPlacement, (forced) => {
  if (forced) {
    detachBridgeListeners()
  } else if (showAutocompletePopup.value && isBridgeActive.value) {
    updateBridgePlacement()
    attachBridgeListeners()
  }
})

watch(
  () => bridgeStore.clientInput,
  () => {
    if (showAutocompletePopup.value) {
      updateBridgePlacement()
    }
  }
)


onUnmounted(() => {
  detachBridgeListeners()
  window.removeEventListener('resize', mobileViewStore.updateIsMobileView);
  unregisterPopupSafe?.()
  deactivatePointerReleaseGuard()
});

onMounted(() => {
  syncPopupSafeElement()
  window.addEventListener('resize', mobileViewStore.updateIsMobileView);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "popupRoot",
    ref: popupRoot,
    onPointerupCapture: guardMobilePointerRelease,
    onTouchendCapture: guardMobilePointerRelease,
    onMouseupCapture: guardMobilePointerRelease,
    onClickCapture: guardMobilePointerRelease
  }, [
    (_unref(showAutocompletePopup))
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: _normalizeClass(["q-autocomplete-mask", {'q-autocomplete-mask--interactive': _unref(isMobileView)}]),
          onPointerdown: _cache[0] || (_cache[0] = _withModifiers(
//@ts-ignore
(...args) => (_unref(popupStore).hideAutocomplete && _unref(popupStore).hideAutocomplete(...args)), ["stop"]))
        }, null, 34))
      : _createCommentVNode("", true),
    _createVNode(AutocompleteInputContainer),
    _withDirectives(_createElementVNode("div", {
      id: "smartsearch-autocomplete__popup-container",
      class: _normalizeClass(["smartsearch-autocomplete__popup-container q-shadow q-p-2 q-bg-body q-rounded q-bg-white", {'smartsearch-autocomplete__popup-container--bridge': isBridgeActive.value}]),
      style: _normalizeStyle(bridgePopupStyle.value)
    }, [
      _createVNode(OverlayLoader, { loading: _unref(loading) }, null, 8, ["loading"]),
      _createVNode(LinearLoader, { loading: _unref(loading) }, null, 8, ["loading"]),
      _createVNode(AutocompleteContent)
    ], 6), [
      [_vShow, _unref(showAutocompletePopup)]
    ])
  ], 544))
}
}

})